import { ref, computed } from 'vue';
import { useExitIntent } from '@/composables/useMarketplaceConversion';
import { notifyPartnerServicesClick } from '@/utils/notifyPartnerServicesClick';
import { useAnalytics } from '@/composables/useAnalytics';
import {
  getLeadSessionProfile,
  isListingLeadSentInSession,
  submitListingLead,
  getColivingDisplayTitle,
  clearLeadSessionProfile,
  markListingLeadSent,
} from '@/services/scrapedLeadSubmit';
import { isAffiliatePartnerListing, openAffiliatePartnerListing } from '@/utils/externalRedirect';

/**
 * Lead capture modals, exit-intent and partner contact on the search page.
 */
export function useSearchLeadOrchestrator(deps) {
  const {
    filters,
    leadCaptureCity,
    searchPerformed,
    searchResults,
    currentUser,
    showToast,
    t,
  } = deps;

  const { trackScrapedLeadSubmitted } = useAnalytics();

  const isPartnerModalVisible = ref(false);
  const isLeadModalVisible = ref(false);
  const isHiveLeadModalVisible = ref(false);
  const leadModalMode = ref('search'); // 'search' | 'listing' | 'exit'
  const leadModalColiving = ref(null);
  const hiveLeadColiving = ref(null);
  const isSilentLeadSubmitting = ref(false);
  /** Bump to re-read sessionStorage (not reactive by default). */
  const leadSessionTick = ref(0);

  function refreshLeadSessionState() {
    leadSessionTick.value += 1;
  }

  function handlePartnerServicesClick() {
    notifyPartnerServicesClick('advanced-search', currentUser.value);
    isPartnerModalVisible.value = true;
  }

  function openLeadModalSearch() {
    leadModalMode.value = 'search';
    leadModalColiving.value = null;
    isLeadModalVisible.value = true;
  }

  function clearLeadSessionAndEdit() {
    clearLeadSessionProfile();
    refreshLeadSessionState();
    openLeadModalSearch();
  }

  function openHiveLeadModal(coliving) {
    if (!coliving?.id) return;
    hiveLeadColiving.value = coliving;
    isHiveLeadModalVisible.value = true;
  }

  function closeHiveLeadModal() {
    isHiveLeadModalVisible.value = false;
    hiveLeadColiving.value = null;
  }

  function handleHiveLeadSubmitted() {
    refreshLeadSessionState();
    closeHiveLeadModal();
  }

  async function openLeadModalListing(coliving) {
    if (!coliving?.id || isSilentLeadSubmitting.value) return;

    if (coliving.isHivePartner) {
      openHiveLeadModal(coliving);
      return;
    }

    // Partner afiliado (Outsite / Blueground / Spacest / Hello AYA)
    if (isAffiliatePartnerListing(coliving)) {
      openAffiliatePartnerListing(coliving, {
        userId: currentUser.value?.uid,
        source: 'coliving-search',
      });
      return;
    }

    const listingId = coliving.id;

    if (isListingLeadSentInSession(listingId)) {
      showToast?.(t('scrapedLeadModal.sessionAlreadySent'));
      return;
    }

    const sessionProfile = getLeadSessionProfile();
    if (sessionProfile) {
      isSilentLeadSubmitting.value = true;
      try {
        const city =
          coliving.propertyLocation ||
          coliving.city ||
          leadCaptureCity.value ||
          '';

        const result = await submitListingLead(listingId, sessionProfile, {
          city,
          searchFilters: getLeadSearchFilters(),
        });

        if (result.ok) {
          const title = getColivingDisplayTitle(coliving);
          refreshLeadSessionState();
          // GA4 + Google Ads + Meta — silent submit también es conversión real
          trackScrapedLeadSubmitted({
            colivingId: listingId,
            colivingNombre: title,
            ciudad: city,
            modo: 'silent',
            leadId: result.leadId || '',
          });
          showToast?.(t('scrapedLeadModal.sessionSentListing', { title }));
          return;
        }

        if (result.status === 409) {
          markListingLeadSent(listingId);
          showToast?.(result.data?.error || t('scrapedLeadModal.sessionDuplicate'));
          return;
        }
      } catch {
        showToast?.(t('scrapedLeadModal.errorConnection'));
      } finally {
        isSilentLeadSubmitting.value = false;
      }
    }

    leadModalMode.value = 'listing';
    leadModalColiving.value = coliving;
    isLeadModalVisible.value = true;
  }

  function openLeadModalExit() {
    leadModalMode.value = 'exit';
    leadModalColiving.value = null;
    isLeadModalVisible.value = true;
  }

  function closeLeadModal() {
    isLeadModalVisible.value = false;
    leadModalMode.value = 'search';
    leadModalColiving.value = null;
  }

  function handleLeadSubmitted() {
    refreshLeadSessionState();
    // El modal muestra éxito + recomendaciones Spacest; cierra con @close-lead
  }

  function getLeadSearchFilters() {
    return {
      priceMax: filters.value?.maxPrice || null,
      petsAllowed: filters.value?.policies?.pets || false,
      maxMinimumStay: filters.value?.maxMinimumStay || null,
    };
  }

  const showLeadCaptureCta = computed(() =>
    searchPerformed.value &&
    !!leadCaptureCity.value &&
    (searchResults.value.some((r) => r.isExternal) || searchResults.value.length === 0)
  );

  const operatorsInCityCount = computed(() => {
    const sources = new Set();
    for (const r of searchResults.value) {
      if (r.isExternal && r.externalSource) {
        sources.add(String(r.externalSource).toLowerCase());
      }
    }
    return sources.size;
  });

  const hasLeadSessionProfile = computed(() => {
    void leadSessionTick.value;
    return !!getLeadSessionProfile();
  });

  useExitIntent({
    canShow: () =>
      searchPerformed.value &&
      !!leadCaptureCity.value &&
      searchResults.value.length >= 3 &&
      searchResults.value.some((r) => r.isExternal) &&
      !isLeadModalVisible.value,
    onTrigger: openLeadModalExit,
  });

  return {
    isPartnerModalVisible,
    isLeadModalVisible,
    isHiveLeadModalVisible,
    leadModalMode,
    leadModalColiving,
    hiveLeadColiving,
    isSilentLeadSubmitting,
    handlePartnerServicesClick,
    openLeadModalSearch,
    openLeadModalListing,
    openHiveLeadModal,
    openLeadModalExit,
    closeLeadModal,
    closeHiveLeadModal,
    handleLeadSubmitted,
    handleHiveLeadSubmitted,
    clearLeadSessionAndEdit,
    getLeadSearchFilters,
    showLeadCaptureCta,
    operatorsInCityCount,
    hasLeadSessionProfile,
  };
}
